import React, { useState, useEffect, useCallback, useMemo } from 'react';
import PageMeta from './PageMeta';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrophy, faFire, faStar, faChessQueen, faDumbbell } from '@fortawesome/free-solid-svg-icons';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../contexts/LanguageContext';
import { t as tActive, formatNumber } from '../i18n';
import RankBadge from './RankBadge';
import RatingHistoryChart from './RatingHistoryChart';
import PurchaseModal from './PurchaseModal';
import '../css/LeaderBoard.css';

const EXPERT_CATEGORIES = [
    { key: 'total_score', icon: 'fa-chart-line' },
    { key: 'best_game', icon: 'fa-gem' },
    { key: 'speed', icon: 'fa-bolt' },
    { key: 'hardest', icon: 'fa-mountain' },
    { key: 'most_solved', icon: 'fa-list-check' },
];

const PRACTICE_CATEGORIES = [
    { key: 'total_score', icon: 'fa-chart-line' },
    { key: 'most_solved', icon: 'fa-list-check' },
    { key: 'perfect', icon: 'fa-bullseye' },
    { key: 'speed', icon: 'fa-bolt' },
    { key: 'hardest', icon: 'fa-mountain' },
];

const CHALLENGE_TYPES = ['pure_swap', 'speed_run', 'long_words', 'perfectionist', 'marathon'];
const PERIODS = ['daily', 'weekly', 'monthly', 'all'];

// Bullet keys under leaderboard.info.<section>; challenge falls back to the ranked section.
const INFO_ITEMS = {
    expert: ['totalScore', 'bestGame', 'speed', 'hardest', 'mostSolved'],
    practice: ['totalScore', 'mostSolved', 'perfect', 'speed', 'hardest', 'compare'],
    rating: ['allTime', 'periods'],
    ranked: ['steps', 'time', 'total', 'compare', 'streak'],
};

// Comparison metrics shared by the ranked and practice boards (labels in leaderboard.metrics.<col>).
// `dir` is the direction that counts as "better" for sorting.
const METRIC_COLUMNS = {
    games: { key: 'gamesPlayed', dir: 'desc' },
    days: { key: 'daysPlayed', dir: 'desc' },
    perfect: { key: 'perfectSolves', dir: 'desc' },
    extra: { key: 'avgExtraSteps', dir: 'asc' },
    time: { key: 'avgTimeSeconds', dir: 'asc' },
    hints: { key: 'hintsUsed', dir: 'asc' },
    streak: { key: 'streak', dir: 'desc' },
    best: { key: 'bestScore', dir: 'desc' },
};

export function formatSeconds(sec) {
    if (sec === null || sec === undefined) return '–';
    const s = Math.round(sec);
    if (s < 60) return tActive('leaderboard.duration.seconds', { s });
    const m = Math.floor(s / 60);
    const rest = s % 60;
    if (m < 60) return tActive('leaderboard.duration.minutes', { m, s: String(rest).padStart(2, '0') });
    return tActive('leaderboard.duration.hours', { h: Math.floor(m / 60), m: String(m % 60).padStart(2, '0') });
}

function formatMetric(colKey, value, entry) {
    if (value === null || value === undefined) return '–';
    switch (colKey) {
        case 'time': return formatSeconds(value);
        case 'extra': return value === 0 ? '0' : `+${formatNumber(value)}`;
        case 'perfect': return entry.gamesPlayed
            ? tActive('leaderboard.perfectValue', { value, rate: formatNumber(entry.perfectRate) })
            : `${value}`;
        case 'streak': return value > 0 ? `🔥 ${value}` : '–';
        default: return value;
    }
}

function LeaderBoard() {
    const { user, isAuthenticated } = useAuth();
    const { t, locale, language } = useTranslation();
    const [gameType, setGameType] = useState('ranked');
    const [timePeriod, setTimePeriod] = useState('daily');
    const [expertCategory, setExpertCategory] = useState('total_score');
    const [practiceCategory, setPracticeCategory] = useState('total_score');
    const [leaderboard, setLeaderboard] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [serverTitle, setServerTitle] = useState('');
    const [challengeType, setChallengeType] = useState(null);
    const [userRank, setUserRank] = useState(null);
    const [userScore, setUserScore] = useState(null);
    const [userEntry, setUserEntry] = useState(null);
    const [ratingMode, setRatingMode] = useState('cumulative');
    const [sort, setSort] = useState(null); // { col, dir } or null for server rank order

    const loadLeaderboard = useCallback(async () => {
        setLoading(true);
        setError(null);

        try {
            let data;
            if (gameType === 'rating') {
                data = await api.fetchRatingLeaderboard(language, 50, '', timePeriod);
                setRatingMode(data.mode || 'cumulative');
            } else if (gameType === 'challenge') {
                data = await api.fetchChallengeLeaderboard(language);
            } else if (gameType === 'expert') {
                data = await api.fetchExpertLeaderboard(language, expertCategory, 50, timePeriod);
            } else if (gameType === 'practice') {
                data = await api.fetchPracticeLeaderboard(language, practiceCategory, 50, timePeriod);
            } else {
                data = await api.fetchLeaderboard(language, timePeriod);
            }
            if (data.success) {
                setLeaderboard(data.entries);
                setServerTitle(data.title);
                setChallengeType(data.challengeType || null);
                setUserRank(data.userRank);
                setUserScore(gameType === 'rating' ? data.userRating : data.userScore);
                setUserEntry(data.userEntry || null);
            } else {
                throw new Error(data.error || 'Failed to load leaderboard');
            }
        } catch (err) {
            console.error('Error loading leaderboard:', err);
            setError(t('leaderboard.loadError'));
        } finally {
            setLoading(false);
        }
    }, [language, gameType, timePeriod, expertCategory, practiceCategory, t]);

    useEffect(() => {
        loadLeaderboard();
    }, [loadLeaderboard]);

    useEffect(() => {
        setSort(null);
    }, [gameType, timePeriod, expertCategory, practiceCategory]);

    const getRankEmoji = (rank) => {
        switch (rank) {
            case 1: return '🥇';
            case 2: return '🥈';
            case 3: return '🥉';
            default: return rank;
        }
    };

    const getRankClass = (rank) => {
        switch (rank) {
            case 1: return 'gold';
            case 2: return 'silver';
            case 3: return 'bronze';
            default: return '';
        }
    };

    // Key into leaderboard.score / leaderboard.yourScore
    const getScoreColumn = () => {
        if (gameType === 'rating') {
            return ratingMode === 'top_movers' ? 'ratingChange' : 'rating';
        }
        if (gameType === 'expert') {
            switch (expertCategory) {
                case 'speed': return 'time';
                case 'hardest': return 'difficulty';
                case 'most_solved': return 'games';
                default: return 'score';
            }
        }
        if (gameType === 'practice') {
            switch (practiceCategory) {
                case 'speed': return 'bestTime';
                case 'hardest': return 'optimalSteps';
                case 'most_solved': return 'games';
                case 'perfect': return 'perfect';
                default: return 'score';
            }
        }
        return 'score';
    };

    // Board title is owned by the frontend; the server's English title is only a fallback.
    const getBoardTitle = () => {
        const withPeriod = (title) => (timePeriod === 'all' ? title : t(`leaderboard.titles.withPeriod.${timePeriod}`, { title }));
        switch (gameType) {
            case 'challenge':
                if (!challengeType) return t('leaderboard.titles.challenge.default');
                return CHALLENGE_TYPES.includes(challengeType) ? t(`leaderboard.titles.challenge.${challengeType}`) : serverTitle;
            case 'rating':
                return timePeriod === 'all'
                    ? t('leaderboard.titles.rating.cumulative')
                    : withPeriod(t('leaderboard.titles.rating.topMovers'));
            case 'expert':
                return withPeriod(t(`leaderboard.titles.expert.${expertCategory}`));
            case 'practice':
                return withPeriod(t(`leaderboard.titles.practice.${practiceCategory}`));
            default:
                return t(`leaderboard.titles.ranked.${timePeriod}`);
        }
    };

    const formatNum = (value, grouping = true) => (typeof value === 'number'
        ? value.toLocaleString(locale, { maximumFractionDigits: 2, useGrouping: grouping })
        : value);

    const formatScore = (entry) => {
        if (gameType === 'rating') {
            if (ratingMode === 'top_movers') {
                return `${entry.ratingChange > 0 ? '+' : ''}${formatNum(entry.ratingChange, false)}`;
            }
            return `${formatNum(entry.rating, false)}${entry.isProvisional ? '?' : ''}`;
        }
        if (gameType === 'practice' && practiceCategory === 'speed') {
            return formatSeconds(entry.score);
        }
        return formatNum(entry.score);
    };

    const showTimePeriod = gameType !== 'challenge';
    const hasMetrics = gameType === 'ranked' || gameType === 'practice';

    // Which comparison columns to show, in order
    const metricColumns = useMemo(() => {
        if (gameType === 'ranked') {
            const cols = ['games', 'perfect', 'extra', 'time', 'hints', 'streak'];
            if (timePeriod !== 'daily') cols.splice(1, 0, 'days');
            return cols;
        }
        if (gameType === 'practice') {
            const cols = ['games', 'perfect', 'extra', 'time', 'hints', 'best'];
            // Don't repeat the category value as a metric column
            const dup = { most_solved: 'games', perfect: 'perfect' }[practiceCategory];
            return dup ? cols.filter(c => c !== dup) : cols;
        }
        return [];
    }, [gameType, timePeriod, practiceCategory]);

    const myUserId = userEntry?.userId ?? user?.id ?? null;
    const isMe = (entry) => myUserId !== null && entry.userId === myUserId;
    const userInTop = userEntry && leaderboard.some(isMe);

    const sortedBoard = useMemo(() => {
        if (!sort) return leaderboard;
        const col = METRIC_COLUMNS[sort.col];
        const key = col ? col.key : 'score';
        const rows = [...leaderboard];
        rows.sort((a, b) => {
            const av = a[key], bv = b[key];
            if (av === null || av === undefined) return 1;
            if (bv === null || bv === undefined) return -1;
            const diff = typeof av === 'number' && typeof bv === 'number' ? av - bv : String(av).localeCompare(String(bv));
            return sort.dir === 'asc' ? diff : -diff;
        });
        return rows;
    }, [leaderboard, sort]);

    const toggleSort = (colKey) => {
        if (!hasMetrics) return;
        const better = colKey === 'score' ? 'desc' : METRIC_COLUMNS[colKey].dir;
        setSort(prev => {
            if (!prev || prev.col !== colKey) return { col: colKey, dir: better };
            if (prev.dir === better) return { col: colKey, dir: better === 'asc' ? 'desc' : 'asc' };
            return null;
        });
    };

    const sortIndicator = (colKey) => {
        if (!hasMetrics) return null;
        if (!sort || sort.col !== colKey) return <span className="sort-hint">⇅</span>;
        return <span className="sort-active">{sort.dir === 'asc' ? '▲' : '▼'}</span>;
    };

    const renderMetricChips = (entry) => (
        <span className="metric-chips">
            {metricColumns.map(c => (
                <span key={c} className="metric-chip" title={t(`leaderboard.metrics.${c}.title`)}>
                    <span className="chip-label">{t(`leaderboard.metrics.${c}.short`)}</span>
                    {formatMetric(c, entry[METRIC_COLUMNS[c].key], entry)}
                </span>
            ))}
        </span>
    );

    const renderRow = (entry, extraClass = '') => (
        <tr
            key={`${extraClass}-${entry.userId}`}
            className={`${getRankClass(entry.rank)} ${isMe(entry) ? 'current-user' : ''} ${extraClass}`}
        >
            <td className="rank-col">
                <span className="rank-number">{getRankEmoji(entry.rank)}</span>
            </td>
            <td className="player-col">
                <span className="player-name">
                    {gameType === 'rating' && entry.rankTier && (
                        <RankBadge tier={entry.rankTier} size="small" showRating={false} />
                    )}
                    <span className={`player-username ${entry.isAnonymous ? 'anonymous' : ''}`}>
                        {entry.username}
                    </span>
                    {isMe(entry) && <span className="you-badge">{t('common.you')}</span>}
                </span>
                {hasMetrics && renderMetricChips(entry)}
            </td>
            <td className="score-col">
                <span className={`score-value ${gameType === 'rating' && ratingMode === 'top_movers' ? (entry.ratingChange > 0 ? 'positive-change' : 'negative-change') : ''}`}>
                    {formatScore(entry)}
                </span>
            </td>
            {hasMetrics ? (
                metricColumns.map(c => (
                    <td key={c} className={`metric-col metric-${c}`}>
                        <span className="games-value">
                            {formatMetric(c, entry[METRIC_COLUMNS[c].key], entry)}
                        </span>
                    </td>
                ))
            ) : (
                (entry.gamesPlayed ?? entry.gamesCompleted) !== undefined && (
                    <td className="games-col">
                        <span className="games-value">{formatNum(entry.gamesPlayed ?? entry.gamesCompleted)}</span>
                    </td>
                )
            )}
        </tr>
    );

    const infoSection = INFO_ITEMS[gameType] ? gameType : 'ranked';
    const rankCardClass = gameType === 'expert' ? 'expert-rank-card' : gameType === 'practice' ? 'practice-rank-card' : '';
    const showRankCard = userRank && (isAuthenticated || userEntry);

    return (
        <div className={`leaderboard-page ${hasMetrics ? 'wide' : ''}`}>
            <PageMeta path="/leaderboard" />
            <div className="leaderboard-header">
                <h1>🏆 {t('leaderboard.heading')}</h1>
                <p className="subtitle">{getBoardTitle()}</p>
            </div>

            {/* Row 1: Game Type Selector */}
            <div className="filter-section">
                <span className="filter-label">{t('leaderboard.filters.mode')}</span>
                <div className="game-type-selector">
                    <button
                        className={`type-button ${gameType === 'ranked' ? 'active' : ''}`}
                        onClick={() => setGameType('ranked')}
                    >
                        <FontAwesomeIcon icon={faTrophy} />
                        <span>{t('leaderboard.types.ranked')}</span>
                    </button>
                    <button
                        className={`type-button practice-button ${gameType === 'practice' ? 'active' : ''}`}
                        onClick={() => { setGameType('practice'); if (timePeriod === 'daily') setTimePeriod('all'); }}
                    >
                        <FontAwesomeIcon icon={faDumbbell} />
                        <span>{t('leaderboard.types.practice')}</span>
                    </button>
                    <button
                        className={`type-button expert-button ${gameType === 'expert' ? 'active' : ''}`}
                        onClick={() => setGameType('expert')}
                    >
                        <FontAwesomeIcon icon={faFire} />
                        <span>{t('leaderboard.types.expert')}</span>
                    </button>
                    <button
                        className={`type-button ${gameType === 'challenge' ? 'active' : ''}`}
                        onClick={() => setGameType('challenge')}
                    >
                        <FontAwesomeIcon icon={faStar} />
                        <span>{t('leaderboard.types.challenge')}</span>
                    </button>
                    <button
                        className={`type-button ${gameType === 'rating' ? 'active' : ''}`}
                        onClick={() => setGameType('rating')}
                    >
                        <FontAwesomeIcon icon={faChessQueen} />
                        <span>{t('leaderboard.types.rating')}</span>
                    </button>
                </div>
            </div>

            {/* Row 2: Time Period Selector (hidden for Challenge) */}
            {showTimePeriod && (
                <div className="filter-section">
                    <span className="filter-label">{t('leaderboard.filters.period')}</span>
                    <div className="time-period-selector">
                        {PERIODS.map(p => (
                            <button
                                key={p}
                                className={`period-button ${timePeriod === p ? 'active' : ''}`}
                                onClick={() => setTimePeriod(p)}
                            >
                                {t(`leaderboard.periods.${p}`)}
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {/* Row 3: Category Sub-Selector (Expert / Practice) */}
            {gameType === 'expert' && (
                <div className="expert-category-selector">
                    {EXPERT_CATEGORIES.map(cat => (
                        <button
                            key={cat.key}
                            className={`category-button ${expertCategory === cat.key ? 'active' : ''}`}
                            onClick={() => setExpertCategory(cat.key)}
                        >
                            <i className={`fa ${cat.icon}`}></i>
                            <span>{t(`leaderboard.categories.${cat.key}`)}</span>
                        </button>
                    ))}
                </div>
            )}
            {gameType === 'practice' && (
                <div className="expert-category-selector practice-category-selector">
                    {PRACTICE_CATEGORIES.map(cat => (
                        <button
                            key={cat.key}
                            className={`category-button ${practiceCategory === cat.key ? 'active' : ''}`}
                            onClick={() => setPracticeCategory(cat.key)}
                        >
                            <i className={`fa ${cat.icon}`}></i>
                            <span>{t(`leaderboard.categories.${cat.key}`)}</span>
                        </button>
                    ))}
                </div>
            )}

            {/* User's Rank Card */}
            {showRankCard && (
                <div className={`user-rank-card ${rankCardClass}`}>
                    <div className="user-rank-main">
                        <div className="user-rank-info">
                            <span className="label">{t('leaderboard.yourRank')}</span>
                            <span className="rank">#{userRank}</span>
                        </div>
                        <div className="user-score-info">
                            <span className="label">{t(`leaderboard.yourScore.${getScoreColumn()}`)}</span>
                            <span className="score">
                                {gameType === 'rating' && ratingMode === 'top_movers' && userScore > 0 ? '+' : ''}
                                {userEntry ? formatScore(userEntry) : formatNum(userScore, gameType !== 'rating')}
                            </span>
                        </div>
                    </div>
                    {hasMetrics && userEntry && (
                        <div className="user-metrics">
                            {metricColumns.map(c => (
                                <div key={c} className="user-metric" title={t(`leaderboard.metrics.${c}.title`)}>
                                    <span className="label">{t(`leaderboard.metrics.${c}.label`)}</span>
                                    <span className="value">{formatMetric(c, userEntry[METRIC_COLUMNS[c].key], userEntry)}</span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* Rating History Chart */}
            {gameType === 'rating' && isAuthenticated && (
                <RatingHistoryChart />
            )}

            {/* Loading State */}
            {loading && (
                <div className="loading-container">
                    <div className="loading-spinner"></div>
                    <p>{t('leaderboard.loading')}</p>
                </div>
            )}

            {/* Error State */}
            {error && (
                <div className="error-container">
                    <i className="fa fa-exclamation-triangle"></i>
                    <p>{error}</p>
                    <button onClick={loadLeaderboard}>{t('common.retry')}</button>
                </div>
            )}

            {/* Leaderboard Table */}
            {!loading && !error && (
                <div className={`leaderboard-container ${hasMetrics ? 'has-metrics' : ''}`}>
                    {leaderboard.length === 0 ? (
                        <div className="empty-state">
                            <i className={`fa ${gameType === 'expert' ? 'fa-fire' : gameType === 'practice' ? 'fa-dumbbell' : 'fa-trophy'}`}></i>
                            <p>
                                {t(`leaderboard.empty.${gameType === 'expert' || gameType === 'practice' ? gameType : 'default'}`)}
                            </p>
                        </div>
                    ) : (
                        <table className="leaderboard-table">
                            <thead>
                                <tr>
                                    <th className="rank-col">{t('leaderboard.columns.rank')}</th>
                                    <th className="player-col">{t('leaderboard.columns.player')}</th>
                                    <th
                                        className={`score-col ${hasMetrics ? 'sortable' : ''}`}
                                        onClick={() => toggleSort('score')}
                                    >
                                        {t(`leaderboard.score.${getScoreColumn()}`)} {sortIndicator('score')}
                                    </th>
                                    {hasMetrics ? (
                                        metricColumns.map(c => (
                                            <th
                                                key={c}
                                                className={`metric-col metric-${c} sortable`}
                                                title={t(`leaderboard.metrics.${c}.title`)}
                                                onClick={() => toggleSort(c)}
                                            >
                                                {t(`leaderboard.metrics.${c}.label`)} {sortIndicator(c)}
                                            </th>
                                        ))
                                    ) : (
                                        (leaderboard[0].gamesPlayed ?? leaderboard[0].gamesCompleted) !== undefined && (
                                            <th className="games-col">{t('leaderboard.columns.games')}</th>
                                        )
                                    )}
                                </tr>
                            </thead>
                            <tbody>
                                {sortedBoard.map((entry) => renderRow(entry))}
                                {userEntry && !userInTop && !sort && (
                                    <>
                                        <tr className="gap-row" key="gap">
                                            <td colSpan={3 + metricColumns.length}>⋯</td>
                                        </tr>
                                        {renderRow(userEntry, 'user-row')}
                                    </>
                                )}
                            </tbody>
                        </table>
                    )}
                </div>
            )}

            {/* Info Section */}
            <div className="leaderboard-info">
                <h3>{t(`leaderboard.info.${infoSection}.title`)}</h3>
                <ul>
                    {INFO_ITEMS[infoSection].map(item => (
                        <li key={item}>{t.rich(`leaderboard.info.${infoSection}.${item}`, { b: <strong /> })}</li>
                    ))}
                </ul>
            </div>
            <PurchaseModal />
        </div>
    );
}

export default LeaderBoard;
